import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const DivisionDash = () => {
  const navigate = useNavigate();
  const [score, setScore] = useState(0);
  const [question, setQuestion] = useState(null);
  const [options, setOptions] = useState([]);
  const [playerLane, setPlayerLane] = useState(1);
  const [obstacles, setObstacles] = useState([]);
  const [powerups, setPowerups] = useState([]);
  const [distance, setDistance] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [timeLeft, setTimeLeft] = useState(90);
  const [gameOver, setGameOver] = useState(false);
  const [lives, setLives] = useState(3);

  const lanes = [0, 1, 2];

  const generateQuestion = () => {
    const divisor = Math.floor(Math.random() * 9) + 2;
    const quotient = Math.floor(Math.random() * 15) + 5;
    const remainder = Math.floor(Math.random() * (divisor - 1));
    const dividend = (quotient * divisor) + remainder;
    
    const correctAnswer = `${quotient}R${remainder}`;
    const wrongAnswers = [
      `${quotient + 1}R${remainder}`,
      `${quotient}R${(remainder + 1) % divisor}`,
      `${quotient - 1}R${remainder}`
    ];
    
    const allOptions = [correctAnswer, ...wrongAnswers]
      .sort(() => Math.random() - 0.5)
      .slice(0, 3);
    
    setQuestion({ dividend, divisor, correctAnswer });
    setOptions(allOptions);
    
    // Place obstacles in lanes
    const newObstacles = allOptions.map((opt, idx) => ({
      id: Date.now() + idx,
      lane: idx,
      answer: opt,
      isCorrect: opt === correctAnswer,
      y: -20
    }));
    
    setObstacles(newObstacles);
  };

  useEffect(() => {
    generateQuestion();
  }, []);

  useEffect(() => {
    if (timeLeft > 0 && !gameOver && lives > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 || lives === 0) {
      setGameOver(true);
    }
  }, [timeLeft, gameOver, lives]);

  useEffect(() => {
    const gameLoop = setInterval(() => {
      if (gameOver) return;

      // Move obstacles down
      setObstacles(prev => {
        const updated = prev.map(o => ({ ...o, y: o.y + speed }));
        
        // Check collisions
        updated.forEach(obstacle => {
          if (obstacle.y > 80 && obstacle.y < 95 && obstacle.lane === playerLane) {
            if (obstacle.isCorrect) {
              setScore(s => s + Math.floor(15 * speed));
              setDistance(d => d + 10);
              setSpeed(s => Math.min(s + 0.1, 3));
              generateQuestion();
            } else {
              setLives(l => l - 1);
              setSpeed(1);
            }
            return [];
          }
        });
        
        // Remove obstacles that passed
        const filtered = updated.filter(o => o.y < 110);
        if (filtered.length === 0) {
          generateQuestion();
        }
        return filtered;
      });
    }, 50);

    return () => clearInterval(gameLoop);
  }, [gameOver, playerLane, speed]);

  const handleKeyPress = (e) => {
    if (gameOver) return;
    
    if (e.key === 'ArrowLeft') {
      setPlayerLane(l => Math.max(0, l - 1));
    } else if (e.key === 'ArrowRight') {
      setPlayerLane(l => Math.min(2, l + 1));
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameOver]);

  const getLanePosition = (lane) => {
    return 16.67 + (lane * 33.33);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-cyan-700 to-teal-500 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 mb-4 text-white">
          <div className="flex justify-between items-center">
            <button onClick={() => navigate('/games')} className="hover:text-yellow-300">
              ← Back
            </button>
            <div className="text-center">
              <h1 className="text-2xl font-bold">🏃‍♂️ Division Dash</h1>
              {question && (
                <div className="text-3xl font-bold mt-2">
                  {question.dividend} ÷ {question.divisor} = ?
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-sm">Speed: {speed.toFixed(1)}x</div>
              <div className="text-2xl font-bold">{score}</div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 text-white text-center">
            <div className="text-2xl">⏱️</div>
            <div className="font-bold">{timeLeft}s</div>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 text-white text-center">
            <div className="text-2xl">❤️</div>
            <div className="font-bold">{'❤️'.repeat(lives)}</div>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 text-white text-center">
            <div className="text-2xl">📏</div>
            <div className="font-bold">{distance}m</div>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 text-white text-center">
            <div className="text-2xl">🚀</div>
            <div className="font-bold">{speed.toFixed(1)}x</div>
          </div>
        </div>

        {!gameOver ? (
          /* Game Area */
          <div className="relative bg-gradient-to-b from-gray-800 to-gray-900 rounded-xl overflow-hidden" style={{ height: '600px' }}>
            {/* Road lanes */}
            <div className="absolute inset-0 flex">
              {lanes.map(lane => (
                <div key={lane} className="flex-1 border-r-2 border-dashed border-white/30" />
              ))}
            </div>

            {/* Obstacles */}
            {obstacles.map(obstacle => (
              <div
                key={obstacle.id}
                className={`absolute transition-all duration-100 ${
                  obstacle.isCorrect ? 'bg-green-500' : 'bg-red-500'
                } rounded-lg p-4 text-white font-bold shadow-lg`}
                style={{
                  left: `${getLanePosition(obstacle.lane)}%`,
                  top: `${obstacle.y}%`,
                  transform: 'translateX(-50%)',
                  width: '100px',
                  height: '80px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <div className="text-center">
                  <div className="text-2xl mb-1">{obstacle.isCorrect ? '✓' : '✗'}</div>
                  <div className="text-lg">{obstacle.answer}</div>
                </div>
              </div>
            ))}

            {/* Player */}
            <div
              className="absolute bottom-8 transition-all duration-200"
              style={{
                left: `${getLanePosition(playerLane)}%`,
                transform: 'translateX(-50%)'
              }}
            >
              <div className="text-6xl">🏃‍♂️</div>
            </div>

            {/* Controls */}
            <div className="absolute bottom-4 left-4 text-white text-sm bg-black/50 p-2 rounded">
              ← → Change Lane
            </div>
          </div>
        ) : (
          /* Game Over */
          <div className="bg-white rounded-xl shadow-2xl p-8 text-center">
            <div className="text-6xl mb-4">🏁</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Race Complete!</h2>
            <div className="text-xl text-gray-600 mb-6">
              <p>Distance: <span className="font-bold text-blue-600">{distance}m</span></p>
              <p>Final Score: <span className="font-bold text-blue-600">{score}</span></p>
              <p>Top Speed: <span className="font-bold text-blue-600">{speed.toFixed(1)}x</span></p>
            </div>
            <div className="space-x-4">
              <button
                onClick={() => window.location.reload()}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-blue-700"
              >
                Race Again
              </button>
              <button
                onClick={() => navigate('/games')}
                className="bg-gray-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-700"
              >
                Back to Games
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DivisionDash;